import React, { useState, useEffect } from 'react';
import { Col, Row, Form } from 'react-bootstrap';
import Modal from '../../.././components/Modal';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faUpload } from '@fortawesome/free-solid-svg-icons';
import { faFileMagnifyingGlass } from '@fortawesome/pro-solid-svg-icons';
import CustomToast from '../../../components/Toast';
import { useSelector } from 'react-redux';
import { selectCurrentUserData } from '../../../features/authentication/authSlice';
import HesSpinner from '../../../components/loadingOverlay/hesSpinner';
import { useNavigate } from 'react-router-dom';
import { NavLink } from 'react-router-dom';
import { useAssignmentContext } from '../context';
import { useData } from '../DataContext';
import DropDown from '../../../components/DropDown';
import { useCreateAssignmentMutation, useImportAssignmentFromXmlMutation } from '../../../features/authentication/assignmentApislice';
import '../../../styles/globalStyle.scss';
import PropTypes from 'prop-types';
import FolderDropdown from '../../../components/FolderDropdown';
import ActivityTypes from '../../../enums/activityTypes';
import UserTypes from '../../../enums/userTypes';

const AssignmentCreateContent = ({ showBrowseContent, showTitle, showCancelButton, handleCancel, onCreateSuccess, fromAssignments }) => {
  const [createAssignment, { isLoading }] = useCreateAssignmentMutation();
  const [importAssignmentFromXml] = useImportAssignmentFromXmlMutation();
  const [isImportXML, setIsImportXML] = useState(false);
  const [isWaiting, setIsWaiting] = useState(false);
  const [files, setFiles] = useState(null);
  const [loadingProgress, setLoadingProgress] = useState('0%');
  const navigate = useNavigate();
  const user = useSelector(selectCurrentUserData);
  const selectedAssignment = useAssignmentContext();
  const [dateBasedKey, setDateBasedKey] = React.useState(new Date().getTime().toString());
  const { courseData, folderData, assignedViaData } = useData();
  const [selectedCourseId, setSelectedCourseId] = useState('');
  const [allCourses, setAllCourses] = useState([]);
  const [selectedFolderId, setSelectedFolderId] = useState(0);
  const [allFolders, setAllFolders] = useState([]);
  const [selectedAssignedViaId, setAssignedViaId] = useState(1);
  const [enteredName, setEnteredName] = useState('');
  const [validName, setValidName] = useState(true);
  const [validCourseSelected, setValidCourseSelected] = useState(true);
  const [validAssignedViaSelected, setValidAssignedViaSelected] = useState(true);
  const [allParents, setAllParents] = useState([]);
  const [selectedFolderName, setSelectedFolderName] = useState('Default Folder');
  const sharedFolder = '*** Shared Assignments ***';

  const handleImportXML = () => {
    setIsImportXML(true);
  };

  const handleCreatePublicAssignment = () => {
    navigate('/assignments/create_public_assignment');
  };

  const handleFileUpload = (e) => {
    e.preventDefault();
    const file = e.target.files[0];
    setFiles(file);
  };

  const resetImportState = () => {
    setIsWaiting(false);
    setLoadingProgress('0%');
  };

  const handleUploadXMLfile = async () => {
    const importFile = files;
    if (!importFile)
      return;

    const fileExtension = importFile.name.split('.').pop().toLowerCase();
    if (fileExtension !== 'xml') {
      CustomToast('Invalid file type. Please select an XML file.', 'warning');
      return;
    }

    setIsWaiting(true);
    const fileName = importFile.name;
    const reader = new FileReader();
    reader.onload = async (e) => {
      const content = e.target.result;
      const parser = new DOMParser();
      const xmlDoc = parser.parseFromString(content, 'text/xml');
      const xmlString = new XMLSerializer().serializeToString(xmlDoc);

      const chunkSize = 10 * 1024 * 1024; // 10MB chunks
      const totalChunks = Math.ceil(xmlString.length / chunkSize);

      if (totalChunks <= 1)
        setLoadingProgress('Importing');

      for (let i = 0; i < totalChunks; i++) {
        const start = i * chunkSize;
        const end = (i + 1) * chunkSize;
        const chunk = btoa(unescape(encodeURIComponent(xmlString.substring(start, end))));
        if (totalChunks > 1)
          setLoadingProgress(Math.round(((i + 1) / totalChunks) * 100) + '%');
        if (!(await uploadChunk(chunk, i + 1, totalChunks, fileName))) break;
      }
    };
    reader.onerror = () => {
      CustomToast('Failed to read XML file.', 'error');
      resetImportState();
    };
    reader.onabort = () => {
      CustomToast('XML file import was cancelled.', 'warning');
      resetImportState();
    };
    reader.readAsText(importFile);
  };

  const uploadChunk = async (chunk, currentChunk, totalChunks, fileName) => {
    const options = { fileName, fileContent: chunk, currentChunk, totalChunks };
    const response = await importAssignmentFromXml(options);

    if (currentChunk == totalChunks) {
      if (response.error) {
        CustomToast('File Uploaded Failed.', 'error');
      } else {
        setFiles(null);
        setIsImportXML(false);
        CustomToast('File Uploaded Successfully.', 'success');
        selectedAssignment.setAssignedDataStatus(Date.now());
      }
      resetImportState();
    } else {
      if (response.error) {
        setFiles(null);
        CustomToast('File Uploaded Failed.', 'error');
        resetImportState();
        return false;
      }
      return true;
    }
  };

  const handleSelectCourse = (event) => {
    setSelectedCourseId(parseInt(event.target.value));
    setValidCourseSelected(parseInt(event.target.value) > 0);
  };

  const handleAssignedViaChange = (event) => {
    setAssignedViaId(parseInt(event.target.value));
    setValidAssignedViaSelected(parseInt(event.target.value) > 0);
  };

  const getAllCoursesAsync = async () => {
    try {
      const obj = {
        id: 0,
        name: 'Select Course'
      };
      const courseArray = [obj, ...courseData];
      courseArray !== null && courseArray !== undefined
        ? setAllCourses(courseArray)
        : setAllCourses([obj]);
    } catch (error) {
      console.log(error);
    }
  };

  const getAllFoldersAsync = async () => {
    try {
      const folderArray = [];
      const parentfolderArray = [];

      folderData.filter(item => item.folderID !== -88 && !item.description.toLowerCase().includes(sharedFolder.toLowerCase())).map((item) => {
        const obj = {
          id: item.folderID,
          name: item.description
        };
        folderArray.push(obj);
        if (item.parent === 0)
          parentfolderArray.push(obj);
      });
      const objData = {
        id: -1,
        name: 'No Parent'
      };
      const parentArrayResponse = [objData, ...parentfolderArray];
      setAllParents(parentArrayResponse);
      setAllFolders(folderArray);
    } catch (error) {
      console.log(error);
    }
  };

  const handleCreate = async () => {
    setValidName(enteredName.length > 0);
    setValidCourseSelected(selectedCourseId > 0);
    setValidAssignedViaSelected(selectedAssignedViaId > 0);

    if (!selectedCourseId > 0 || !selectedAssignedViaId > 0 || !enteredName.length > 0) {
      return;
    }

    if (isLoading) return;

    var options = {
      CourseId: selectedCourseId,
      Description: enteredName,
      FolderId: selectedFolderId,
      AssignedViaId: selectedAssignedViaId,
      Activity: 5
    };
    try {
      const response = await createAssignment(options);
      if (response.error) {
        if (response.error.data.errors)
          CustomToast(response.error.data.errors.Description, 'error');
        else
          CustomToast(response.error.data.error, 'error');
      } else if (response.data) {
        CustomToast('Assignment Created Successfully', 'success');
        setSelectedCourseId(null);
        setAssignedViaId(1);
        setSelectedFolderId(0);
        setEnteredName('');
        if (onCreateSuccess) {
          onCreateSuccess(response.data.assignmentId, { id: selectedAssignedViaId, name: assignedViaData.find(a => a.id == selectedAssignedViaId).name });
        } else {
          navigate('/questionsSearch/assignment/' + response.data.assignmentId + '/castle_questions/questions', {
            replace: true, state: {
              assignedViaDetails: {
                id: selectedAssignedViaId,
                name: assignedViaData.find(a => a.id == selectedAssignedViaId).name
              }
            }
          });
        }
      }
    } catch (error) {
      console.log(error);
    }
  };

  useEffect(() => {
    getAllCoursesAsync();
  }, [courseData]);

  useEffect(() => {
    getAllFoldersAsync();
  }, [folderData]);

  const handleClear = () => {
    setSelectedCourseId('');
    setSelectedFolderId(0);
    setAssignedViaId(1);
    setEnteredName('');
    setSelectedFolderName('Default Folder');
  };

  return (
    <>
      {showTitle && <div className="row align-items-center mb-4">
        <div className="col-lg-5">
          <h4 className="secondary-title mb-lg-0">Create New Assignment</h4>
        </div>
      </div>
      }
      <Row>
        <Col className={`${showBrowseContent ? 'col-xl-8' : ''}`}>
          <Row>
            <Col className={`${fromAssignments ? 'col-md-4' : 'col-md-6'} mb-3`}>
              <DropDown
                key={dateBasedKey}
                label="Select Course"
                value={allCourses}
                onChangeHandler={(e) => handleSelectCourse(e)}
                selectedValue={selectedCourseId}
                showValidation={!validCourseSelected}
                dropDownName={'Course'}
              />
            </Col>
            <Col className={`${fromAssignments ? 'col-md-4' : 'col-md-6'} mb-3`}>
              <Form.Group>
                <Form.Label htmlFor="classDropdown">Select Folder</Form.Label>
                <FolderDropdown
                  allFolders={allFolders}
                  allParents={allParents}
                  dropdownId="folderList"
                  selectedId={selectedFolderId}
                  setSelectedFolderId={setSelectedFolderId}
                  selectedFolderName={selectedFolderName}
                  setSelectedFolderName={setSelectedFolderName}
                  activityType={ActivityTypes.Assignment}
                />
              </Form.Group>
            </Col>
            <Col className={`${fromAssignments ? 'col-md-4' : 'col-md-6'} mb-3`}>
              <DropDown
                key={dateBasedKey}
                label="Select Assigned Via"
                value={assignedViaData}
                onChangeHandler={(e) => handleAssignedViaChange(e)}
                selectedValue={selectedAssignedViaId}
                showValidation={!validAssignedViaSelected}
                dropDownName={'Assigned Via'}
              />
            </Col>
            <Col className={`${fromAssignments ? 'col-md-8' : 'col-md-6'} mb-3`}>
              <label className="form-label">Assignment Name</label>
              <input
                type="text"
                className={`form-control ${!validName ? 'border-danger' : ''}`}
                value={enteredName}
                onChange={(e) => setEnteredName(e.target.value)}
              />
              {!validName && <p className='text-danger'>Assignment Name Is Required.</p>}
            </Col>
            {fromAssignments &&
              <Col className='mt-4 pt-2'>
                <div className='d-flex gap-2 justify-content-end'>
                  <button className="btn btn-castleWhite min-w-150" onClick={handleClear}>
                    Clear
                  </button>
                  <button className="btn btn-castleRed min-w-150" onClick={handleCreate}>
                    Create
                  </button>
                </div>
              </Col>
            }
            <Col className="col-12 d-flex flex-column flex-sm-row gap-3 align-items-center">
              {showTitle &&
                <div className="d-flex flex-wrap gap-3">
                  <button
                    className="btn-link secondary-link px-0 text-start"
                    onClick={handleCreatePublicAssignment}>
                    <i className="fa-solid fa-layer-group me-2"></i>Create From Public Assignments
                  </button>
                  {[UserTypes.SuperAdministrator, UserTypes.Sales, UserTypes.SuperSales, UserTypes.Trainer].includes(user?.UserType) && (
                    <button
                      className="btn-link secondary-link px-0 text-start"
                      onClick={() => handleImportXML()}>
                      <i className="fa-solid fa-file-import me-2"></i>Import Assignment
                    </button>
                  )}
                </div>
              }
              <div className="ms-auto d-flex flex-column flex-sm-row create-btn-group create-btn-group">
                {!fromAssignments &&
                  <>
                    <div className='p-2'>
                      <button className="btn btn-castleGray option-btn btn-width" onClick={handleCancel}>
                        Cancel
                      </button>
                    </div>
                    <div className='p-2'>
                      <button className="btn outline-btn btn-width" onClick={handleCreate}>
                        Create
                      </button>
                    </div>
                  </>
                }
              </div>
            </Col>
          </Row>
        </Col>
        {showBrowseContent && <Col className="col-xl-4 mt-4 mt-xl-0">
          <NavLink
            to={'/questionsSearch'}
            className="select-questions h-100 p-4 d-flex flex-column align-items-center justify-content-center text-center text-decoration-none">
            <span className="mb-3 d-flex align-items-center justify-content-center nav-link-browse-content">
              <FontAwesomeIcon color="#82A9E2" icon={faFileMagnifyingGlass} />
            </span>
            <h4>Browse Content</h4>
            <p className="mb-0">Select the questions first, then create a new assignment.</p>
          </NavLink>
        </Col>
        }
      </Row>

      {isImportXML && (
        <Modal
          contentWidth="700px"
          isOpen={isImportXML}
          titleName={'Upload Assignment'}
          onClose={() => {
            setIsImportXML(false);
            setFiles(null);
            setIsWaiting(false);
          }}>
          {!isWaiting ? (
            <>
              <p>Select an XML Assignment file.</p>
              <div className="upload_xml">
                <div className="row mb-3">
                  <div className="col-12">
                    <div
                      className="custom-upload position-relative d-flex flex-column justify-content-center align-items-center p-5 text-center"
                      onDragOver={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        e.dataTransfer.dropEffect = 'copy';
                      }}
                      onDragEnter={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                      }}
                      onDrop={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        if (e.dataTransfer.files.length > 1) {
                          CustomToast('Only 1 file can be uploaded at a time.', 'warning');
                          return;
                        }
                        if (e.dataTransfer.files.length > 0)
                          setFiles(e.dataTransfer.files[0]);
                      }}>
                      <input
                        onChange={handleFileUpload}
                        className="form-control position-absolute w-100 h-100 top-0 start-0"
                        type="file"
                        accept=".xml"
                      />
                      <span className="d-block mb-2">
                        {' '}
                        <FontAwesomeIcon icon={faUpload} />
                      </span>
                      <p className="mb-0">
                        <strong>Click to upload</strong> or Drag and drop file
                      </p>
                      {files && (
                        <div className="mt-3">
                          <p>{files.name}</p>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </>
          ) : (
            <div className="d-flex justify-content-center align-items-center h-40">
              <HesSpinner />
              <span className="ms-3">{loadingProgress === 'Importing' ? 'Importing...' : `${loadingProgress} Uploaded...`}</span>
            </div>
          )}
          <div className="d-flex justify-content-end gap-3 pt-2">
            <button
              className="btn btn-castleGray print-btn"
              onClick={() => {
                setIsImportXML(false);
                setFiles(null);
                resetImportState();
              }}>
              Cancel
            </button>
            <button
              className={'btn btn-castleRed print-btn'}
              onClick={handleUploadXMLfile}
              disabled={isWaiting}>
              Import
            </button>
          </div>
        </Modal>
      )}
    </>
  );
};

AssignmentCreateContent.propTypes = {
  showBrowseContent: PropTypes.bool,
  showTitle: PropTypes.bool,
  showCancelButton: PropTypes.bool,
  handleCancel: PropTypes.func,
  onCreateSuccess: PropTypes.func,
  fromAssignments: PropTypes.bool
};

export default AssignmentCreateContent;
